<template>
    <div class="flex items-center">
        <p class="mr-2">{{ helpfulMessage }}</p>

        <div class="shrink-0 inline-flex shadow-sm rounded-md">
            <button
                :data-test="`helpful-${reviewId}`"
                @click="voteHelpful(true)"
                type="button"
                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:outline-none"
            >
                <span class="sr-only">Helpful</span>
                <ThumbUpIcon class="h-5 w-5" aria-hidden="true" />
            </button>

            <button
                :data-test="`not-helpful-${reviewId}`"
                @click="voteHelpful(false)"
                type="button"
                class="-ml-px relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:outline-none"
            >
                <span class="sr-only">Not Helpful</span>
                <ThumbDownIcon class="h-5 w-5" aria-hidden="true" />
            </button>
        </div>

        <p
            :class="[
                message.type === 'success'
                    ? 'text-nutreance-green'
                    : 'text-red-600',
                'ml-2',
            ]"
            v-if="message.message"
        >
            {{ message.message }}
        </p>
    </div>
</template>

<script setup lang="ts">
import { inject, reactive, ref } from "vue";
import { ThumbUpIcon, ThumbDownIcon } from "@heroicons/vue/outline";
import { voteReview } from "../../api/reviews";
import { queueReviewVote } from "../../functions/reviewVoteQueue";
import type { AppEmitter } from "../../emitter";

const props = defineProps<{
    reviewId: number;
    helpfulMessage: string;
}>();

const emitter = inject<AppEmitter>("mitt")!;

const helpfulMessage = ref(props.helpfulMessage);

/*
 * Over the bus rather than straight into the ref, because one review can be rendered twice: a
 * product's top positive or critical review is picked by helpfulness while the recent list is
 * ordered by date, so the same card often appears in both. Assigning locally would leave the
 * other copy showing the old count.
 */
emitter.on("reviewVoteRecorded", (payload) => {
    const vote = payload as { reviewId: number; helpfulMessage: string };

    if (vote.reviewId === props.reviewId) {
        helpfulMessage.value = vote.helpfulMessage;
    }
});

const message = reactive<{ type: string | null; message: string | null }>({
    type: null,
    message: null,
});

function voteHelpful(helpful: boolean) {
    queueReviewVote(props.reviewId, () => sendVote(helpful));
}

function sendVote(helpful: boolean) {
    return voteReview(props.reviewId, helpful)
        .then((data) => {
            message.type = "success";
            message.message = data.message;
            emitter.emit("reviewVoteRecorded", {
                reviewId: props.reviewId,
                helpfulMessage: data.helpfulMessage,
            });
        })
        .catch((error: any) => {
            if (
                typeof error.response === "undefined" ||
                !Object.prototype.hasOwnProperty.call(error.response, "data")
            ) {
                return;
            }

            if (
                Object.prototype.hasOwnProperty.call(
                    error.response.data,
                    "errors",
                )
            ) {
                const field = Object.keys(error.response.data.errors)[0];
                message.type = "error";
                message.message = error.response.data.errors[field][0];
            } else {
                message.type = "unauthenticated";
                message.message = error.response.data.message;
            }
        });
}
</script>
